/**
 * Settings Directory
 *
 * The map of every settings surface, backing the settings hub page
 * (/:orgSlug/settings) and the settings paths OmniSearch deep-links to. Like
 * settings-sections.ts this is intentionally DATA ONLY (enums and plain
 * objects, no components) so it can live in the main bundle.
 *
 * Note the hub page renders the `org` and `admin` scopes only — personal
 * settings live in the user menu (Account settings). The `personal`
 * entries stay here because they map module hrefs (/tasks/mine, /calendar)
 * to their settings paths, which the path-coverage tests and the directory
 * hook both rely on.
 *
 * Section lists are NOT duplicated here. Hub entries reference the frozen
 * registries in settings-sections.ts (MODULE_SETTINGS_SECTIONS keyed by
 * navHref, ADMIN_SETTINGS_SECTIONS, USER_SETTINGS_TABS); ids and labels there
 * are load-bearing for OmniSearch and the help-content path audit in
 * packages/database. Per-section facts (personal preference rows, org-admin
 * gated rows, extra permission gates) live on the section defs there too.
 *
 * When a module gains or loses a settings page, update MODULE_SETTINGS_PATHS
 * and SETTINGS_DIRECTORY together.
 */

import {
  AccountType,
  ModuleTypeEnum,
  PermissionActionEnum,
} from '@scrambl/shared'

// Module hrefs that have a dedicated settings page. Value is the
// org-relative settings path used by the hub, OmniSearch and the settings
// search index: the Settings shell hosts module settings at
// /settings/modules/<key> (key = nav href with slashes as dashes, see
// pages/settings-hub/lib/shell-routes.ts). The module's own
// <module>/settings route renders the same page inside the module (its
// Settings tab), so both addresses work — neither redirects to the other.
// Note: /notes/settings is a dead redirect and is not listed.
export const MODULE_SETTINGS_PATHS: Record<string, string> = {
  '/tasks/mine': '/settings/modules/tasks-mine',
  '/calendar': '/settings/modules/calendar',
  '/projects': '/settings/modules/projects',
  '/tickets': '/settings/modules/tickets',
  '/events': '/settings/modules/events',
  '/contacts': '/settings/modules/contacts',
  '/deals': '/settings/modules/deals',
  '/entities': '/settings/modules/entities',
  '/suppliers': '/settings/modules/suppliers',
  '/team': '/settings/modules/team',
  '/financials': '/settings/modules/financials',
  '/timesheets': '/settings/modules/timesheets',
  '/contracts': '/settings/modules/contracts',
  '/invoices': '/settings/modules/invoices',
  '/expenses': '/settings/modules/expenses',
  '/quotes': '/settings/modules/quotes',
  '/products': '/settings/modules/products',
  '/knowledge-base': '/settings/modules/knowledge-base',
}

/**
 * What a user needs to access the settings behind an entry. Absent = personal
 * settings, always editable by their owner. The gate mirrors the target
 * page's own checkPermission call — keep them in sync when a page changes
 * its gate.
 */
export type SettingsEditGate =
  | { kind: 'module'; module: ModuleTypeEnum; action: PermissionActionEnum }
  | { kind: 'orgAdmin' }

export interface SettingsDirectoryEntry {
  /** Stable id: 'personal:profile', 'org:/tickets', 'admin:/admin/settings' */
  id: string
  scope: 'personal' | 'org' | 'admin'
  /**
   * For org entries: key into MODULE_SETTINGS_PATHS + MODULE_SETTINGS_SECTIONS
   * and the href of the matching NavItem in useNavigationItems (label, icon,
   * and module visibility are resolved from it). For admin entries: the href
   * of the matching adminNavItems item.
   */
  navHref?: string
  /** Display label for entries with no NavItem (personal entries). */
  label?: string
  description: string
  /**
   * Org-relative deep-link path for entries not derivable from
   * MODULE_SETTINGS_PATHS (personal + admin entries).
   */
  settingsPath?: string
  editGate?: SettingsEditGate
  /** Mirrors NavItem.accountTypes for entries with no NavItem. */
  accountTypes?: AccountType[]
  keywords?: string[]
  /**
   * Admin entries only: the page's sub-pages, listed as rows under the group
   * in the Settings hub. `subPage` is the route segment after the admin key
   * (pages/settings-hub/lib/admin-hosts.ts); omitted for the page itself.
   */
  adminSections?: {
    id: string
    label: string
    subPage?: string
    keywords?: string[]
  }[]
}

export const SETTINGS_DIRECTORY: SettingsDirectoryEntry[] = [
  // ---- Personal ----------------------------------------------------------
  {
    id: 'personal:profile',
    scope: 'personal',
    label: 'Account settings',
    description:
      'Your account, skills, notification preferences, and connected apps',
    // Hosted on the Personal stack of the Settings shell (hash tabs).
    settingsPath: '/settings/personal',
    keywords: [
      'account',
      'profile',
      'notifications',
      'integrations',
      'skills',
      'preferences',
      'timezone',
    ],
  },
  {
    id: 'personal:my-task-statuses',
    scope: 'personal',
    navHref: '/tasks/mine',
    label: 'My Work',
    description: 'Personal task statuses and your recycle bin',
    keywords: ['task statuses', 'my work', 'recycle bin'],
  },
  {
    id: 'personal:calendar',
    scope: 'personal',
    navHref: '/calendar',
    label: 'Calendar',
    description: 'Connect Google or Outlook calendars and choose which to show',
    keywords: ['calendar', 'google', 'outlook', 'sync'],
  },

  // ---- Org modules -------------------------------------------------------
  // editGate mirrors each settings page's own checkPermission call (verified
  // 2026-08: products gates on INVENTORY, timesheets on TIME_ENTRY).
  {
    id: 'org:/projects',
    scope: 'org',
    navHref: '/projects',
    description:
      'Statuses, task types, cycle templates, custom fields, and defaults',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.PROJECT,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/tickets',
    scope: 'org',
    navHref: '/tickets',
    description:
      'Ticket types, inboxes, domains, macros, alerts, and the customer widget',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.TICKET,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/events',
    scope: 'org',
    navHref: '/events',
    description:
      'Event, session, and agenda types, roles, reminders, and defaults',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.EVENT,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/contacts',
    scope: 'org',
    navHref: '/contacts',
    description: 'Lifecycle, scoring, categories, custom fields, and defaults',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.CONTACT,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/deals',
    scope: 'org',
    navHref: '/deals',
    description: 'Pipeline stages, scoring, custom fields, and defaults',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.DEAL,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/entities',
    scope: 'org',
    navHref: '/entities',
    description:
      'Lifecycle, scoring, custom fields, and client portal configuration',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.ENTITY,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/contracts',
    scope: 'org',
    navHref: '/contracts',
    description:
      'Contract types, legal statuses, reminders, and retainer plans',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.CONTRACT,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/invoices',
    scope: 'org',
    navHref: '/invoices',
    description: 'Numbering, branding, sending, reminders, and payment methods',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.INVOICE,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/team',
    scope: 'org',
    navHref: '/team',
    description:
      'Rates, billing, allocation, holidays, compensation, and skills',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.TEAM,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/financials',
    scope: 'org',
    navHref: '/financials',
    description: 'Budget categories, templates, and overhead allocation',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.FINANCIALS,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/timesheets',
    scope: 'org',
    navHref: '/timesheets',
    description: 'Time format, pay periods, entry rules, and approval workflow',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.TIME_ENTRY,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/products',
    scope: 'org',
    navHref: '/products',
    description: 'Category types, movement reasons, and custom fields',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.INVENTORY,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/suppliers',
    scope: 'org',
    navHref: '/suppliers',
    description: 'Lifecycle stages, payment terms, and custom fields',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.SUPPLIER,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/expenses',
    scope: 'org',
    navHref: '/expenses',
    description: 'Categories, statuses, payment methods, and defaults',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.EXPENSE,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/quotes',
    scope: 'org',
    navHref: '/quotes',
    description: 'Quote acceptance and invoicing behavior',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.QUOTE,
      action: PermissionActionEnum.MANAGE,
    },
  },
  {
    id: 'org:/knowledge-base',
    scope: 'org',
    navHref: '/knowledge-base',
    description: 'Delivery channels and the public help center',
    editGate: {
      kind: 'module',
      module: ModuleTypeEnum.KNOWLEDGE_BASE,
      action: PermissionActionEnum.MANAGE,
    },
  },

  // ---- Administration ----------------------------------------------------
  // Admin entries carry explicit labels (the adminNavItems label for
  // /admin/settings is just "Settings", which is ambiguous inside the hub).
  {
    id: 'admin:/admin/members',
    scope: 'admin',
    navHref: '/admin/members',
    label: 'Members',
    description: 'Manage people, AI agents, and access levels',
    settingsPath: '/settings/administration/members',
    editGate: { kind: 'orgAdmin' },
    keywords: ['members', 'users', 'invite', 'roles', 'permissions', 'seats'],
    adminSections: [
      { id: 'members', label: 'Members', keywords: ['invite', 'seats'] },
      {
        id: 'agents',
        label: 'AI Agents',
        subPage: 'agents',
        keywords: ['agents', 'AI', 'personality', 'permissions', 'automation'],
      },
      {
        id: 'permissions',
        label: 'Access Levels',
        subPage: 'permissions',
        keywords: ['permission sets', 'roles', 'access'],
      },
      {
        id: 'external-organizations',
        label: 'External Organizations',
        subPage: 'external-organizations',
        keywords: ['external', 'partner', 'guest'],
      },
    ],
  },
  {
    id: 'admin:/admin/integrations',
    scope: 'admin',
    navHref: '/admin/integrations',
    label: 'Integrations',
    description: 'Organization-wide connections like Slack and QuickBooks',
    settingsPath: '/settings/administration/integrations',
    editGate: { kind: 'orgAdmin' },
    keywords: ['integrations', 'slack', 'quickbooks', 'connect'],
  },
  {
    id: 'admin:/admin/settings',
    scope: 'admin',
    navHref: '/admin/settings',
    label: 'Organization Settings',
    description: 'Organization identity, workspace defaults, and finance',
    // Hosted on the Settings tab of the Settings shell; /admin/settings is
    // the same page inside admin mode.
    settingsPath: '/settings',
    editGate: { kind: 'orgAdmin' },
    keywords: [
      'organization',
      'general',
      'workspace',
      'finance',
      'timezone',
      'terminology',
    ],
  },
  {
    id: 'admin:/admin/logs',
    scope: 'admin',
    navHref: '/admin/logs',
    label: 'Audit Trail',
    description:
      'Who changed what, who accessed a record, and the field-level history',
    settingsPath: '/settings/administration/logs',
    editGate: { kind: 'orgAdmin' },
    accountTypes: [AccountType.ORGANIZATION],
    keywords: [
      'audit',
      'log',
      'history',
      'compliance',
      'who changed',
      'access log',
      'trail',
    ],
  },
  {
    id: 'admin:/admin/archive',
    scope: 'admin',
    navHref: '/admin/archive',
    label: 'Archive',
    description:
      'Deleted and archived records across every module, and how to restore them',
    settingsPath: '/settings/administration/archive',
    editGate: { kind: 'orgAdmin' },
    accountTypes: [AccountType.ORGANIZATION],
    keywords: [
      'archive',
      'archived',
      'deleted',
      'restore',
      'recover',
      'undelete',
      'recycle bin',
      'trash',
      'bin',
    ],
  },
  {
    id: 'admin:/admin/data',
    scope: 'admin',
    navHref: '/admin/data',
    label: 'Data',
    description:
      'Bring your data over from Harvest, Toggl, Clockify, monday.com, Accelo or Scoro',
    settingsPath: '/settings/administration/data',
    editGate: { kind: 'orgAdmin' },
    accountTypes: [AccountType.ORGANIZATION],
    adminSections: [
      {
        id: 'bring-your-data',
        label: 'Bring your data',
        keywords: ['import', 'migrate', 'migration', 'concierge'],
      },
    ],
    keywords: [
      'data',
      'import',
      'migrate',
      'migration',
      'move data',
      'switch',
      'harvest',
      'toggl',
      'clockify',
      'monday',
      'accelo',
      'scoro',
      'concierge',
    ],
  },
  {
    id: 'admin:/admin/subscription',
    scope: 'admin',
    navHref: '/admin/subscription',
    label: 'Subscription & Billing',
    description: 'Plan, seats, usage, and payment details',
    settingsPath: '/settings/administration/subscription',
    editGate: { kind: 'orgAdmin' },
    accountTypes: [AccountType.ORGANIZATION],
    adminSections: [
      {
        id: 'billing',
        label: 'Plan & Billing',
        keywords: ['payment', 'seats'],
      },
      { id: 'usage', label: 'Usage', subPage: 'usage', keywords: ['credits'] },
      { id: 'plans', label: 'Plans', subPage: 'plans', keywords: ['upgrade'] },
    ],
    keywords: [
      'billing',
      'plan',
      'payment',
      'upgrade',
      'seats',
      'subscription',
    ],
  },
]
